import type { ImportReport, SkippedAnalysisGame } from "./contracts";

/** Shared private import copy; transport messages are never trusted as product text. */
export const importOutcomeUncertainMessage = "Не удалось подтвердить результат импорта. Партии могли сохраниться. Обновите список перед повторной загрузкой";

const rejectionStatus = {
  "illegal-move": 400,
  "invalid-fen": 400,
  "invalid-pgn": 400,
  "source-too-large": 413,
  "request-too-large": 413,
  "too-many-games": 400,
  "move-limit": 400,
  "variation-depth-limit": 400,
  "unsupported-variant": 400,
  "result-conflict": 400,
  conflict: 409,
  "import-failed": 500,
  "invalid-request": 400,
  "unsupported-media-type": 415,
} as const;
type ImportErrorCode = keyof typeof rejectionStatus;
type ImportErrorDetails = { gameIndex?: number; move?: string; line?: number; column?: number };

export function formatImportError(
  code: ImportErrorCode,
  { gameIndex, move, line, column }: ImportErrorDetails = {},
): string {
  const prefix = gameIndex === undefined ? "" : `Партия ${gameIndex}: `;
  if (code === "source-too-large") return "Размер PGN не должен превышать 1 МиБ";
  if (code === "request-too-large") return "Размер запроса не должен превышать 8 МиБ";
  if (code === "too-many-games") return "PGN не должен содержать более 100 партий";
  if (code === "conflict") return "Не удалось сохранить партии из-за одновременного изменения данных. Повторите импорт";
  if (code === "import-failed") return "Не удалось импортировать партии";
  if (code === "invalid-request") return "Некорректный запрос импорта PGN";
  if (code === "unsupported-media-type") return "Требуется JSON в кодировке UTF-8 без сжатия";
  if (code === "variation-depth-limit") return "Вложенность вариантов PGN не должна превышать 32";
  if (code === "move-limit") return gameIndex === undefined ? "PGN не должен содержать более 20000 ходов, включая варианты" : `${prefix}не более 5000 ходов, включая варианты`;
  if (code === "invalid-pgn") return `Не удалось разобрать PGN${line === undefined ? "" : `. Строка ${line}${column === undefined ? "" : `, столбец ${column}`}`}`;
  if (code === "invalid-fen") return `${prefix}некорректный FEN`;
  if (code === "unsupported-variant") return `${prefix}неподдерживаемый вариант шахмат`;
  if (code === "result-conflict") return `${prefix}результат в заголовке не совпадает с результатом партии`;
  return `${prefix}недопустимый ход${move === undefined ? "" : ` «${move}»`}`;
}

const positiveInteger = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value > 0;
// Limit displayed tokens, without interpreting chess notation or trusting response.message.
const safeMove = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= 32 && !/[\s{}\[\]<>"«»]/u.test(value);
const isImportErrorCode = (code: unknown): code is ImportErrorCode => typeof code === "string" && Object.prototype.hasOwnProperty.call(rejectionStatus, code);

export function readImportRejection(status: number, payload: unknown): string | undefined {
  if (typeof payload !== "object" || payload === null || !("code" in payload) ||
    !("message" in payload) || typeof payload.message !== "string" ||
    !isImportErrorCode(payload.code) || rejectionStatus[payload.code] !== status) return;
  const gameIndex = "gameIndex" in payload && positiveInteger(payload.gameIndex) && payload.gameIndex <= 100 ? payload.gameIndex : undefined;
  const move = "move" in payload && safeMove(payload.move) ? payload.move : undefined;
  const line = "line" in payload && positiveInteger(payload.line) ? payload.line : undefined;
  const column = line !== undefined && "column" in payload && positiveInteger(payload.column) ? payload.column : undefined;
  const message = formatImportError(payload.code, { gameIndex, move, line, column });
  const path = payload.code === "illegal-move" && "movePath" in payload &&
    Array.isArray(payload.movePath) && payload.movePath.length > 0 && payload.movePath.every(safeMove)
    ? payload.movePath : undefined;
  return `${message}${formatMoveContext(path)}`;
}

function formatMoveContext(path: string[] | undefined): string {
  // Display nearby correction context; the full validator path stays in the response.
  const displayedMoveContextLength = 8;
  const context = path && path.length > displayedMoveContextLength
    ? ["…", ...path.slice(-displayedMoveContextLength)] : path;
  return context ? `. Вариант: ${context.join(" → ")}` : "";
}

export function readImportAcknowledgement(payload: unknown, status: number, submittedOriginalCount: number): ImportReport | undefined {
  if (!positiveInteger(submittedOriginalCount) || submittedOriginalCount > 100 ||
    typeof payload !== "object" || payload === null || Object.keys(payload).length !== 4 ||
    Object.keys(payload).some((key) => !["problemSetId", "importedCount", "skippedGames", "openingWarnings"].includes(key)) ||
    !("problemSetId" in payload) || !("importedCount" in payload) || !("skippedGames" in payload) ||
    !("openingWarnings" in payload) || !Array.isArray(payload.openingWarnings) ||
    !Array.isArray(payload.skippedGames) || !Number.isSafeInteger(payload.importedCount) ||
    typeof payload.importedCount !== "number" || payload.importedCount < 0 ||
    payload.importedCount + payload.skippedGames.length !== submittedOriginalCount ||
    !((status === 201 && positiveInteger(payload.problemSetId) && payload.importedCount > 0) ||
      (status === 200 && payload.problemSetId === null && payload.importedCount === 0 && payload.openingWarnings.length === 0))) return;
  let previousIndex = 0;
  for (const game of payload.skippedGames as unknown[]) {
    if (typeof game !== "object" || game === null || !("gameIndex" in game) ||
      !positiveInteger(game.gameIndex) || game.gameIndex > submittedOriginalCount || game.gameIndex <= previousIndex ||
      !("code" in game) || typeof game.code !== "string" ||
      !["student-color-unknown", "move-limit", "unsupported-variant", "invalid-fen", "illegal-move"].includes(game.code)) return;
    const allowedFields = game.code === "illegal-move" ? ["gameIndex", "code", "move", "movePath"] : ["gameIndex", "code"];
    if (Object.keys(game).some((key) => !allowedFields.includes(key)) ||
      ("move" in game && !safeMove(game.move)) ||
      ("movePath" in game && (!Array.isArray(game.movePath) || game.movePath.length === 0 || !game.movePath.every(safeMove)))) return;
    previousIndex = game.gameIndex;
  }
  let previousOpeningId = 0;
  for (const warning of payload.openingWarnings as unknown[]) {
    if (typeof warning !== "object" || warning === null || Object.keys(warning).length !== 2 ||
      Object.keys(warning).some((key) => !["openingId", "code"].includes(key)) ||
      !("openingId" in warning) || !positiveInteger(warning.openingId) || warning.openingId <= previousOpeningId ||
      !("code" in warning) || typeof warning.code !== "string" ||
      !["invalid-fen", "invalid-pgn", "illegal-move"].includes(warning.code)) return;
    previousOpeningId = warning.openingId;
  }
  return payload as ImportReport;
}

export function formatSkippedAnalysisGame(game: SkippedAnalysisGame): string {
  if (game.code === "student-color-unknown") return `Партия ${game.gameIndex}: не удалось определить цвет ученика`;
  return `${formatImportError(game.code, game)}${game.code === "illegal-move" ? formatMoveContext(game.movePath) : ""}`;
}
